import { createContext,  useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import API_ENDPOINTS from '../config/api';
import Swal from 'sweetalert2';
import { toast, ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'sweetalert2/dist/sweetalert2.min.css';
import '../css/sweetalert-custom.css'

const normalizeUserRole = (role) => {
    if (!role) return 'Guest';

    const normalized = String(role).trim().toLowerCase();
    if (['admin', 'administrator'].includes(normalized)) return 'Admin';
    if (['corporate', 'company', 'organization'].includes(normalized)) return 'Corporate';
    if (['host', 'owner', 'property_manager', 'property-manager'].includes(normalized)) return 'Host';
    if (['blogger', 'writer'].includes(normalized)) return 'Blogger';
    return 'Guest';
};

const normalizeUser = (userData) => {
    if (!userData) return null;
    return {
        ...userData,
        role: normalizeUserRole(userData.role),
    };
};

export const UserContext = createContext();

export default function UserProvider({ children }) {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [allusers, setAllusers] = useState([]);
    const [subscriptionUsage, setSubscriptionUsage] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [organizations, setOrganizations] = useState([]);


    useEffect(() => {
        const checkAuthStatus = async () => {
            const token = localStorage.getItem('access_token');
            if (!token) return;

            try {
                const response = await axios.get(API_ENDPOINTS.CUSTOM.AUTHENTICATED_USER, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUser(normalizeUser(response.data?.data || response.data));
            } catch (error) {
                console.error("Auth Check Error: ", error.response?.data);
                if (error.response?.status === 401) {
                    await refreshToken();
                }
            }
        };

        checkAuthStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const loginUser = async (userdata) => {
        try {
            const response = await axios.post(API_ENDPOINTS.CUSTOM.LOGIN, userdata);
            const accessToken = response.data?.data?.token?.access_token || response.data?.access_token;
            const refreshTokenStr = response.data?.data?.token?.refresh_token || response.data?.refresh_token;

            localStorage.setItem('access_token', accessToken);
            if (refreshTokenStr) {
                localStorage.setItem('refresh_token', refreshTokenStr);
            }
            const userResponse = await axios.get(API_ENDPOINTS.CUSTOM.AUTHENTICATED_USER, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            setUser(normalizeUser(userResponse.data?.data || userResponse.data));
            console.log(userResponse.data?.data || userResponse.data);
            toast.success('Login successful!', {
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
            navigate('/');
        } catch (error) {
            console.error("Login Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : "Login failed!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Login Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                iconColor: '#FF385C',
            });
        }
    };

    const logoutUser = async () => {
        try {
            await axios.post(API_ENDPOINTS.CUSTOM.LOGOUT, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
            });
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setUser(null);
            toast.success('Logout successful!', {
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
            navigate('/');
        } catch (error) {
            console.error("Logout Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : "Logout failed!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Logout Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                iconColor: '#FF385C',
            });
        }
    };

    const forgotPassword = async (email) => {
        try {
            await axios.post(API_ENDPOINTS.CUSTOM.FORGOT_PASSWORD, { email });
            toast.success('Verification code sent to your email!', {
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
        } catch (error) {
            console.error("Forgot Password Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : "Failed to send verification code!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Forgot Password Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                iconColor: '#FF385C',
            });
        }
    };

    const resetPassword = async (data) => {
        try {
            await axios.post(API_ENDPOINTS.CUSTOM.RESET_PASSWORD, data);
            toast.success('Password reset successful!', {
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
            navigate('/');
        } catch (error) {
            console.error("Reset Password Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : "Failed to reset password!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Reset Password Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                iconColor: '#FF385C',
            });
        }
    };

    const refreshToken = async () => {
        try {
            const response = await axios.post(API_ENDPOINTS.CUSTOM.REFRESH, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('refresh_token')}` }
            });
            localStorage.setItem('access_token', response.data.access_token);
        } catch (error) {
            console.error("Refresh Token Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : "Failed to refresh token!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Refresh Token Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                iconColor: '#FF385C',
            });
            logoutUser();
        }
    };

    const registerUser = async (userdata) => {
        try {
            const response = await axios.post(API_ENDPOINTS.CUSTOM.REGISTER, userdata);
            console.log(response.data);
            toast.success('Registration successful! Please check your email for verification.', {
                position: "top-left",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
            navigate('/verification', { state: { email: userdata.email } });
        } catch (error) {
            console.error("Registration Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : "Registration failed!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Registration Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 5000,
                iconColor: '#FF385C',
            });
        }
    };

    const registerHostUser = async (hostData) => {
        try {
            const response = await axios.post(API_ENDPOINTS.USER.REGISTER_HOST, hostData);
            const accessToken = response.data?.data?.token?.access_token || response.data?.access_token;
            const refreshTokenStr = response.data?.data?.token?.refresh_token || response.data?.refresh_token;

            if (accessToken) {
                localStorage.setItem('access_token', accessToken);
            }
            if (refreshTokenStr) {
                localStorage.setItem('refresh_token', refreshTokenStr);
            }
            const registeredUser = response.data?.data?.user || response.data?.user;
            if (registeredUser) {
                setUser(normalizeUser(registeredUser));
            }
            toast.success('Host account created successfully! Welcome to SkyStay.', {
                position: "top-left",
                autoClose: 2500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
                transition: Slide,
            });
            navigate('/dashboard');
        } catch (error) {
            console.error("Host Registration Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && (error.response.data.message || error.response.data.error)
                ? (error.response.data.message || error.response.data.error)
                : "Host registration failed!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Host Registration Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 5000,
                iconColor: '#FF385C',
            });
        }
    };

    const fetchActiveSubscription = useCallback(async () => {
        const token = localStorage.getItem('access_token');
        if (!token) return null;

        setSubscriptionLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.PROPERTY.MY_SUBSCRIPTION, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data?.data || response.data;
            setSubscriptionUsage(data);
            return data;
        } catch (error) {
            console.error("Error fetching subscription: ", error);
            return null;
        } finally {
            setSubscriptionLoading(false);
        }
    }, []);

    const subscribeToPlan = async (planId, currency = 'KES', subscriberType = 'user', subscriberId = 0) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            toast.error('Please log in to subscribe to a plan.');
            return false;
        }

        try {
            const response = await axios.post(
                API_ENDPOINTS.PROPERTY.CREATE_SUBSCRIPTION,
                {
                    plan_id: planId,
                    currency,
                    subscriber_type: subscriberType,
                    subscriber_id: subscriberId || (user ? user.id : 0),
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Subscription activated successfully!', {
                position: "top-left",
                autoClose: 2000,
                transition: Slide,
            });
            await fetchActiveSubscription();
            return response.data;
        } catch (error) {
            console.error("Subscription activation failed: ", error.response?.data);
            const message = error.response?.data?.message || 'Failed to activate subscription.';
            toast.error(message);
            throw error;
        }
    };

    const cancelSubscription = async () => {
        const token = localStorage.getItem('access_token');
        if (!token) return false;

        try {
            await axios.post(API_ENDPOINTS.PROPERTY.CANCEL_SUBSCRIPTION, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.info('Subscription auto-renewal has been cancelled.', {
                position: "top-left",
                autoClose: 2000,
                transition: Slide,
            });
            await fetchActiveSubscription();
            return true;
        } catch (error) {
            console.error("Failed to cancel subscription: ", error.response?.data);
            toast.error('Failed to cancel subscription auto-renewal.');
            return false;
        }
    };

    const checkFeatureEntitlement = async (featureName) => {
        const token = localStorage.getItem('access_token');
        if (!token) return { entitled: false, message: 'Not authenticated' };

        try {
            const response = await axios.get(API_ENDPOINTS.PROPERTY.FEATURE_ENTITLEMENT(featureName), {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data?.data || response.data;
        } catch (error) {
            console.error(`Failed to check entitlement for ${featureName}:`, error);
            return { entitled: false, feature: featureName, message: error.response?.data?.message || 'Check failed' };
        }
    };

    const fetchMyOrganizations = useCallback(async () => {
        const token = localStorage.getItem('access_token');
        if (!token) return [];

        try {
            const response = await axios.get(API_ENDPOINTS.USER.MY_ORGANIZATIONS, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const orgs = response.data?.data || response.data || [];
            setOrganizations(orgs);
            return orgs;
        } catch (error) {
            console.error("Failed to fetch organizations: ", error);
            return [];
        }
    }, []);

    const createOrganization = async (orgData) => {
        const token = localStorage.getItem('access_token');
        if (!token) return null;

        try {
            const response = await axios.post(API_ENDPOINTS.USER.ORGANIZATIONS, orgData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Organization created successfully!', {
                position: "top-left",
                autoClose: 2000,
                transition: Slide,
            });
            await fetchMyOrganizations();
            return response.data?.data || response.data;
        } catch (error) {
            console.error("Failed to create organization: ", error.response?.data);
            toast.error(error.response?.data?.message || 'Failed to create organization.');
            throw error;
        }
    };

    const verifyUser = async (userdata) => {
        try {
            const response = await axios.post(API_ENDPOINTS.CUSTOM.VERIFY, userdata);
            console.log(response.data);
            toast.success('Verification successful!', {
                position: "top-left",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
            navigate('/'); // or wherever you want to navigate on successful verification
        } catch (error) {
            console.error("Verification Error: ", error.response?.data);
            const errorMessage = error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : "Verification failed!";
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: 'Verification Error',
                text: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                iconColor: '#FF385C',
            });
        }
    };

    const getallusers = useCallback(async () => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            toast.error('No token found, please log in.');
            return;
        }
    
        try {
            const response = await axios.get(API_ENDPOINTS.CUSTOM.ALL_USERS, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log("Get all users response :", response.data);
            setAllusers(response.data); // Update the state
        } catch (error) {
            console.error("Error fetching users: ", error.response?.data);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access, please log in again.');
                navigate('/dashboard');
            } else {
                toast.error('Failed to fetch users.');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Dependency array should be empty if this function doesn't rely on external variables
    
    const updateUser = async (userData) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            toast.error('No token found, please log in.');
            return;
        }
    
        try {
            // Make PUT request to update user profile
            await axios.put(API_ENDPOINTS.CUSTOM.UPDATE_USER, userData, {
                headers: { Authorization: `Bearer ${token}` }
            });
    
            // After successful update, fetch updated user data from backend
            const getUserResponse = await axios.get(API_ENDPOINTS.CUSTOM.GET_USER, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Update local user state with fetched updated user data
            setUser(normalizeUser(getUserResponse.data?.data || getUserResponse.data?.user || getUserResponse.data));
    
            toast.success('User profile updated successfully!',{
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
        } catch (error) {
            console.error("Error updating user: ", error.response?.data);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access, please log in again.');
                navigate('/dashboard');
            } else {
                toast.error('Failed to update user profile.');
            }
        }
    };

    const updateUserStatus = async (userId, status) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            toast.error('No token found, please log in.');
            return;
        }
    
        try {
            // Make PUT request to update user status
            await axios.put(API_ENDPOINTS.CUSTOM.UPDATE_USER_STATUS(userId), { account_status: status }, {
                headers: { Authorization: `Bearer ${token}` }
            });
    
            // After successful update, fetch updated user data from backend
            const getUserResponse = await axios.get(API_ENDPOINTS.CUSTOM.ALL_USERS, {
                headers: { Authorization: `Bearer ${token}` }
            });
    
            // Update local user state with fetched updated user data
            setAllusers(getUserResponse.data);
    
            toast.success('User status updated successfully!',{
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
        } catch (error) {
            console.error("Error updating user status: ", error.response?.data);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access, please log in again.');
                navigate('/dashboard');
            } else {
                toast.error('Failed to update user status.');
            }
        }
    };

    const updateUserRole = async (userId, role) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            toast.error('No token found, please log in.');
            return;
        }
    
        try {
            // Make PUT request to update user role
            await axios.put(API_ENDPOINTS.CUSTOM.UPDATE_USER_ROLE(userId), { role }, {
                headers: { Authorization: `Bearer ${token}` }
            });
    
            // After successful update, fetch updated user data from backend
            const getUserResponse = await axios.get('/allusers', {
                headers: { Authorization: `Bearer ${token}` }
            });
    
            // Update local user state with fetched updated user data
            setAllusers(getUserResponse.data);
    
            toast.success('User role updated successfully!',{
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
        } catch (error) {
            console.error("Error updating user role: ", error.response?.data);
            if (error.response?.status === 401) {
                toast.error('Unauthorized access, please log in again.');
                navigate('/dashboard');
            } else {
                toast.error('Failed to update user role.');
            }
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
          const credential = credentialResponse.credential;
          // const decoded = jwtDecode(credential);
    
          // Send the Google credential to your backend
          const response = await axios.post(API_ENDPOINTS.CUSTOM.GOOGLE_LOGIN, {
            credential,
          });
    
          // Handle the response from the backend
          if (response.status === 200 || response.status === 201) {
            // Assuming the response contains the user data
            const user = response.data;
            // Update the user context
            setUser(normalizeUser(user));
            // Store tokens in localStorage
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('access_token', response.data.access_token);
            if (response.data.refresh_token) {
                localStorage.setItem('refresh_token', response.data.refresh_token);
            }
            toast.success('Google Login successful!', {
                position: "top-left",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Slide,
                closeButton: false,
            });
            navigate('/'); // Adjust the route as necessary
            window.location.reload();
          } else {
            console.error('Google login failed', response);
          }
        } catch (error) {
          console.error('Google login error', error);
        }
    };

    const contextData = {
        user,
        setUser,
        allusers,
        getallusers,
        subscriptionUsage,
        subscriptionLoading,
        organizations,
        updateUser,
        updateUserStatus,
        updateUserRole,
        loginUser,
        logoutUser,
        forgotPassword,
        resetPassword,
        refreshToken,
        registerUser,
        registerHostUser,
        verifyUser,
        fetchActiveSubscription,
        subscribeToPlan,
        cancelSubscription,
        checkFeatureEntitlement,
        fetchMyOrganizations,
        createOrganization,
        handleGoogleSuccess,
    };

    return (
        <UserContext.Provider value={contextData}>
            {children}
            <ToastContainer />
        </UserContext.Provider>
    );
}
